/**
 * Firestore adapter for `rooms/{roomId}`: the only module that knows this
 * collection's documents, the per-structure query, and the transaction.
 *
 *   watchRoomsForStructure  the `rooms` query for one structure, live
 *   readRoomFromServer      one room, past the cache (after a server write)
 *   applyRoomMutations      ONE transaction for a batch of mutations applied
 *                           in order — a planned write (operations on the
 *                           id'd arrays, the patch merged over them) or a
 *                           removal by predicate (the belt the floor plan's
 *                           delete keeps beside the store's save) —
 *                           `lastModified` stamped once. The repository's
 *                           per-room queue decides what a batch holds.
 *
 * Rooms cross `parseRoomDoc` on the way in: `safeParse`, raw record passed
 * through with the issues logged when it fails. The room store's own read
 * normalisation (`normalizeRoomData`, `healPersistedRuns`) stays in the hook
 * for now; it is pure and moves into the repository with Step C.
 */
import {
  collection,
  doc,
  getDocFromServer,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import type { DocumentData } from 'firebase/firestore';
import { db } from '../client';
import { stripUndefined } from '../../../utils/firestoreSafe';
import { applyOpeningOps } from '../../../utils/openingArrayOps';
import { RoomDocSchema } from '../../schemas/room.schema';
import type {
  RoomBatchResult,
  RoomDoc,
  RoomMutation,
} from '../../types/room.types';

const roomRef = (roomId: string) => doc(db, 'rooms', roomId);

/**
 * Rooms that failed the schema and were said so, once each: a room that does
 * not match is passed through raw on EVERY snapshot it appears in, and a
 * console.error per snapshot per room is noise that buries the next real
 * one.
 */
const reportedRooms = new Set<string>();
export const __resetRoomSchemaReportsForTests = () => reportedRooms.clear();

export const parseRoomDoc = (
  roomId: string,
  data: DocumentData | undefined
): RoomDoc => {
  const raw = { ...(data || {}), id: roomId };
  const parsed = RoomDocSchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  if (!reportedRooms.has(roomId)) {
    reportedRooms.add(roomId);
    console.error(
      `[rooms.adapter] rooms/${roomId} does not match the room schema; passing the raw record through`,
      parsed.error.issues.slice(0, 5)
    );
  }
  return raw as unknown as RoomDoc;
};

export interface RoomsSnapshot {
  rooms: RoomDoc[];
  /** True for the SDK's optimistic echo of this client's own write. */
  hasPendingWrites: boolean;
}

export function watchRoomsForStructure(
  structureId: string,
  onNext: (snapshot: RoomsSnapshot) => void,
  onError: (error: Error) => void
): () => void {
  const q = query(
    collection(db, 'rooms'),
    where('structureId', '==', structureId)
  );
  // Parsed once per CHANGE, not once per snapshot: a query snapshot carries
  // every room of the structure (up to 16, the largest 876 KB), and parsing
  // them all on every write to any one of them is work the screen never
  // sees. The cache holds the parsed copy of each document by id and takes
  // only what `docChanges()` says moved.
  const parsed = new Map<string, RoomDoc>();
  return onSnapshot(
    q,
    (snapshot) => {
      for (const change of snapshot.docChanges()) {
        if (change.type === 'removed') parsed.delete(change.doc.id);
        else
          parsed.set(
            change.doc.id,
            parseRoomDoc(change.doc.id, change.doc.data())
          );
      }
      onNext({
        rooms: snapshot.docs.map(
          (d) => parsed.get(d.id) ?? parseRoomDoc(d.id, d.data())
        ),
        hasPendingWrites: snapshot.metadata.hasPendingWrites,
      });
    },
    (error) => onError(error)
  );
}

export async function readRoomFromServer(
  roomId: string
): Promise<RoomDoc | null> {
  const snap = await getDocFromServer(roomRef(roomId));
  return snap.exists() ? parseRoomDoc(roomId, snap.data()) : null;
}

/**
 * A room array as the server holds it, as an ARRAY. Some rooms were once
 * written with their element arrays as maps keyed by id (the room store's
 * `normalizeRoomData` exists for them on the read side); an op set applied
 * to a map would throw inside the transaction and lose the save.
 */
const serverArray = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value);
  return [];
};

/**
 * Apply a batch of room mutations IN ORDER in ONE transaction: every write's
 * operations on the arrays, its patch merged over them, every removal by
 * predicate against the array as the mutations before it left it, and
 * `lastModified` stamped once. One transaction per room at a time is the
 * repository's per-room queue; this is what a batch of its items runs as.
 *
 * Composed in order rather than merged: a save and the floor plan's belt
 * removal that overlap on one room land as one commit instead of two that
 * contend (the delete's `failed-precondition` retry seen on the emulator),
 * and a remove followed by an add of the same id keeps its meaning.
 */
export async function applyRoomMutations(
  roomId: string,
  mutations: RoomMutation[]
): Promise<RoomBatchResult> {
  const ref = roomRef(roomId);
  let skipped: string[] = [];
  let written: Record<string, unknown[]> = {};
  let removed: number[] = [];
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new Error(`Room ${roomId} not found`);
    }
    const serverData = (snap.data() || {}) as Record<string, unknown>;
    // The arrays as the mutations so far have left them; read from the
    // server on first touch.
    const arrays = new Map<string, unknown[]>();
    const arrayOf = (field: string): unknown[] => {
      if (!arrays.has(field)) arrays.set(field, serverArray(serverData[field]));
      return arrays.get(field)!;
    };
    const patch: Record<string, unknown> = {};
    skipped = [];
    written = {};
    removed = [];
    for (const mutation of mutations) {
      if (mutation.kind === 'write') {
        for (const [field, value] of Object.entries(mutation.write.patch)) {
          patch[field] = value;
          // A whole-field patch of an array is the array from here on, so a
          // later op set in the batch composes on it.
          if (Array.isArray(value)) arrays.set(field, value);
        }
        for (const [field, ops] of Object.entries(mutation.write.ops)) {
          const applied = applyOpeningOps(arrayOf(field), ops);
          arrays.set(field, applied.array);
          for (const id of applied.skippedUpsertIds) {
            const tag = `${field}:${id}`;
            if (!skipped.includes(tag)) skipped.push(tag);
          }
        }
        removed.push(0);
        continue;
      }
      const current = arrayOf(mutation.field);
      const kept = current.filter(
        (element) =>
          !(
            element && mutation.shouldRemove(element as Record<string, unknown>)
          )
      );
      removed.push(current.length - kept.length);
      if (kept.length !== current.length) arrays.set(mutation.field, kept);
    }
    const updates: Record<string, unknown> = { ...patch };
    for (const [field, array] of arrays) {
      // An array only READ (a removal that removed nothing) is not written —
      // with one exception: a legacy MAP-shaped field. `serverArray` turned
      // it into an array the server does not hold, so any batch that touches
      // the field writes it back as an array (and fires the room's
      // listeners once), even when the batch changed none of its elements.
      // That is the one-time normalisation such a room was due anyway.
      if (array === serverData[field] && !(field in patch)) continue;
      updates[field] = array;
      written[field] = array;
    }
    if (Object.keys(updates).length === 0) return;
    tx.update(ref, {
      ...stripUndefined(updates),
      lastModified: serverTimestamp(),
    });
  });
  if (skipped.length) {
    // Not an error: someone deleted it, and an edit must not resurrect a
    // deleted element.
    console.warn(
      `Room ${roomId} save skipped elements deleted elsewhere:`,
      skipped.join(', ')
    );
  }
  return { skipped, written, removed };
}
